// ============================================================================
// Envoi des leads vers le webhook n8n — en 2 temps.
//   Le POST part vers /api/lead (route serveur) qui relaie vers LEAD_WEBHOOK_URL,
//   de sorte que l'URL du webhook n'est jamais exposée au client.
//
//   • partial_submission — dès que le contact (nom / email / tél) est validé,
//     avant le QCM (le lead est capturé au plus tôt, même s'il abandonne).
//   • full_submission    — à la fin du QCM, avec les réponses + qualification.
//
//   Payload (aligné sur le format attendu par n8n) :
//     { name, mail, num, company, funnel, Q1..Qn, type, ...utms, metadata }
//
//   `funnel` (= "vsl" | "masterclass" | "meet") dit de quelle LP vient le lead : c'est
//   la clé de branchement côté n8n. Les questions Q1..Qn suivent l'ORDRE du
//   QCM du funnel (cf. QCM_STEPS dans ./form), leur intitulé est repris dans
//   metadata.qN_label — donc un funnel qui n'a pas le même QCM reste lisible.
// ============================================================================

import { FormData, Option, QCM_STEPS, isQualified } from "./form";
import { loadFunnelKind } from "./funnel-store";
import { getStoredUtms } from "./utm";

export type LeadStage = "partial_submission" | "full_submission";

/** RDV pris sur cal.com depuis /meet — la conversion confirmée du funnel. */
export type BookingLead = {
  name?: string;
  email?: string;
  phone?: string;
  startTime?: string;
  endTime?: string;
  uid?: string;
};

const FORM_NAME = "Bootcamp Manager Augmenté";

// value technique -> libellé lisible (celui affiché à l'utilisateur).
const labelOf = (options: Option[], value: string) =>
  options.find((o) => o.value === value)?.label ?? value;

function buildPayload(stage: LeadStage, data: FormData): Record<string, unknown> {
  const utms = getStoredUtms();
  const funnel = loadFunnelKind();

  const metadata: Record<string, unknown> = {
    form_name: FORM_NAME,
    funnel,
    is_company_bool: false,
    submitted_at: new Date().toISOString(),
  };

  const payload: Record<string, unknown> = {
    name: data.nomComplet,
    mail: data.email,
    num: data.phone,
    company: "",
    funnel,
    type: stage,
    ...utms,
    metadata,
  };

  if (stage === "full_submission") {
    // Q1..Qn dans l'ordre du QCM de CE funnel (+ intitulés en metadata).
    QCM_STEPS[funnel].forEach((step, i) => {
      const n = i + 1;
      payload[`Q${n}`] = labelOf(step.options, data[step.key]);
      metadata[`q${n}_label`] = step.label;
    });
    metadata.qualified = isQualified(data);
  }

  return payload;
}

/**
 * RDV confirmé (/meet) : même webhook, `type: "booking_confirmed"`. Nom, email
 * et téléphone viennent de la réservation cal.com (ce que la personne a saisi).
 */
export function sendBooking(b: BookingLead): void {
  if (typeof window === "undefined") return;
  const payload = {
    name: b.name ?? "",
    mail: b.email ?? "",
    num: b.phone ?? "",
    company: "",
    funnel: loadFunnelKind(),
    type: "booking_confirmed",
    ...getStoredUtms(),
    metadata: {
      form_name: FORM_NAME,
      funnel: loadFunnelKind(),
      booking_uid: b.uid ?? "",
      booking_start: b.startTime ?? "",
      booking_end: b.endTime ?? "",
      submitted_at: new Date().toISOString(),
    },
  };
  post(payload);
}

/**
 * Captation directe hors QCM (/nl newsletter, /wa communauté) : même webhook,
 * `funnel` explicite, `type` = ce qui s'est passé.
 */
export function sendOptin(
  funnel: "newsletter" | "whatsapp" | "masterclass",
  type: "newsletter_signup" | "whatsapp_join" | "templates_requested",
  contact: { name?: string; email?: string; phone?: string } = {}
): void {
  if (typeof window === "undefined") return;
  post({
    name: contact.name ?? "",
    mail: contact.email ?? "",
    num: contact.phone ?? "",
    company: "",
    funnel,
    type,
    ...getStoredUtms(),
    metadata: { form_name: FORM_NAME, funnel, submitted_at: new Date().toISOString() },
  });
}

function post(payload: unknown): void {
  try {
    fetch("/api/lead", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      /* échec silencieux : ne jamais bloquer l'expérience */
    });
  } catch {
    /* idem — le funnel prime sur l'envoi */
  }
}

/**
 * Envoie le lead au webhook (fire-and-forget : n'interrompt jamais le funnel,
 * même en cas d'échec réseau). `keepalive` permet au POST de survivre à une
 * navigation immédiate (ex. redirection vers /ty).
 */
export function sendLead(stage: LeadStage, data: FormData): void {
  if (typeof window === "undefined") return;
  post(buildPayload(stage, data));
}
